import {
    getOptions,
    InputField,
    Invoice,
    PaymentPlanIntervalType,
    ProviderPaymentPlan,
    SelectField,
} from '@enter/core';
import { Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { DialogContext } from 'services';
import { RootStore } from 'stores';
import * as Yup from 'yup';
import * as S from './payment-plan-dialog.styles';

interface IPaymentPlanDialogProps {
    invoice: Invoice;
    paymentPlan: ProviderPaymentPlan | undefined;
    onClose: () => void;
}

interface ILineItem {
    amountPerCycle: number;
    intervalCount: number;
    intervalSpan: PaymentPlanIntervalType;
}

const schema = Yup.object().shape({
    amountPerCycle: Yup.number().min(1).required('Amount Per Cycle is invalid number'),
    intervalCount: Yup.number().min(1).required('Interval Count is invalid number'),
    intervalSpan: Yup.string().required('Interval Span is invalid'),
});

const initial: ILineItem = {
    amountPerCycle: 0,
    intervalCount: 1,
    intervalSpan: PaymentPlanIntervalType.DAY,
};

export const PaymentPlanDialog: React.FunctionComponent<IPaymentPlanDialogProps> = inject('rootStore')(
    observer(
        (props: IPaymentPlanDialogProps): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const { claimStore } = rootStore;
            const { invoice, paymentPlan, onClose } = props;
            const dialogContext = useContext(DialogContext);
            const maxLength = 100;

            const paymentPlanPreview = (values: ILineItem): string => {
                const { amountPerCycle, intervalCount, intervalSpan } = values;
                const interval = `${_.toLower(intervalSpan)}${intervalCount === 1 ? '' : 's'}`;
                return `Charge $${amountPerCycle} every ${intervalCount} ${interval}`;
            };
            const handleCancel = (): void => {
                onClose && onClose();
            };
            const handleAddPaymentPlan = async (
                values: ILineItem,
                { setSubmitting }: FormikActions<any>,
            ): Promise<void> => {
                if (invoice && invoice.id) {
                    setSubmitting(true);
                    try {
                        const { amountPerCycle, intervalCount, intervalSpan } = values;
                        await claimStore.addInvoicePaymentPlan(invoice.id, amountPerCycle, intervalCount, intervalSpan);
                    } catch (err) {
                        dialogContext.showError(err);
                    }
                    setSubmitting(false);
                }
                onClose && onClose();
            };
            const handleDisablePaymentPlan = async (): Promise<void> => {
                if (paymentPlan && paymentPlan.id) {
                    await claimStore.disablePaymentPlan(paymentPlan.id);
                }
                onClose && onClose();
            };
            const renderAddPaymentPlanDialog = (): React.ReactElement => {
                return (
                    <S.DialogBody>
                        <Formik initialValues={initial} validationSchema={schema} onSubmit={handleAddPaymentPlan}>
                            {(formik: FormikProps<ILineItem>): React.ReactElement => (
                                <>
                                    <S.InputRow>
                                        <S.InputAmount>
                                            <S.Label>Amount Per Cycle</S.Label>
                                            <S.FormField
                                                name="amountPerCycle"
                                                maxLength={maxLength}
                                                component={InputField}
                                                type="number"
                                            />
                                        </S.InputAmount>
                                        <S.InputAmount>
                                            <S.Label>Interval Count</S.Label>
                                            <S.FormField
                                                name="intervalCount"
                                                maxLength={maxLength}
                                                component={InputField}
                                                type="number"
                                            />
                                        </S.InputAmount>
                                        <S.InputAmount>
                                            <S.Label>Interval Span</S.Label>
                                            <S.FormField
                                                name="intervalSpan"
                                                maxLength={maxLength}
                                                component={SelectField}
                                                options={getOptions(PaymentPlanIntervalType, {
                                                    allowEmpty: true,
                                                })}
                                            />
                                        </S.InputAmount>
                                    </S.InputRow>
                                    <S.InputRow>
                                        <S.InputPreview>{paymentPlanPreview(formik.values)}</S.InputPreview>
                                    </S.InputRow>
                                    <S.ButtonGroup
                                        changed={true}
                                        useNotify={false}
                                        onCancel={handleCancel}
                                        onSave={formik.submitForm}
                                        saveLabel="Save"
                                        saveNotice="Item added"
                                    />
                                </>
                            )}
                        </Formik>
                    </S.DialogBody>
                );
            };

            const renderDisablePaymentPlanDialog = (): React.ReactElement => {
                return (
                    <S.DialogBody>
                        <S.InputRow>Are you sure you want to disable this payment plan?</S.InputRow>
                        <S.ButtonGroup
                            changed={true}
                            useNotify={false}
                            onCancel={handleCancel}
                            onSave={handleDisablePaymentPlan}
                            saveLabel="OK"
                            saveNotice="Payment plan disabled"
                        />
                    </S.DialogBody>
                );
            };

            if (paymentPlan) {
                return renderDisablePaymentPlanDialog();
            } else {
                return renderAddPaymentPlanDialog();
            }
        },
    ),
);
